import mixpanel, {
  Callback,
  Config,
  Dict,
  RequestOptions,
} from 'mixpanel-browser'
import { isDevelop } from '../utils/Common'

const MIXPANEL_PROJECT_KEY = process.env.REACT_APP_MIXPANEL_PROJECT_KEY
const MIXPANEL_DEBUG_MODE = !!+(
  process.env.REACT_APP_MIXPANEL_DEBUG_MODE ?? '0'
)
export class MixpanelManager {
  private static isInitialized: boolean = false

  /**
   * Initializes the mixpanel using corresponding .env variables.
   *
   * Use REACT_APP_MIXPANEL_DEBUG_MODE='1' to enable debug mode.
   *
   * Do nothing when app is launched in develop mode or the Mixpanel is not initialized.
   *
   * May throw an error when REACT_APP_MIXPANEL_PROJECT_KEY is undefined.
   */
  static initialize() {
    if (isDevelop()) {
      return
    }

    if (this.isInitialized) {
      console.warn('Mixpanel Manager is already initialized')
      return
    }

    if (!MIXPANEL_PROJECT_KEY) {
      throw new Error('MIXPANEL_PROJECT_KEY is undefined')
    }

    mixpanel.init(MIXPANEL_PROJECT_KEY, {
      debug: MIXPANEL_DEBUG_MODE,
      track_pageview: true,
      persistence: 'localStorage',
    })

    this.isInitialized = true
  }

  static get people() {
    if (isDevelop() || !this.isInitialized) {
      return null
    }

    return mixpanel.people
  }

  /**
   * Identify a user with a unique ID to track user activity across devices, tie a user to their events, and create a user profile.
   *
   * If this method is never called, unique visitors are tracked using a UUID generated the first time they visit the site.
   *
   * Do nothing when app is launched in develop mode or the Mixpanel is not initialized.
   *
   * @param unique_id String that uniquely identifies a user. If not provided, the distinct_id currently in the persistent store (cookie or localStorage) will be used.
   */
  static identify(unique_id?: string) {
    if (isDevelop() || !this.isInitialized) {
      return
    }

    mixpanel.identify(unique_id)
  }

  /**
   * Clears super properties and generates a new random distinct_id for this instance.
   *
   * Useful for clearing data when a user logs out.
   *
   * Do nothing when app is launched in develop mode or the Mixpanel is not initialized.
   */
  static resest() {
    if (isDevelop() || !this.isInitialized) {
      return
    }

    mixpanel.reset()
  }

  /**
   * Track an event
   *
   * Do nothing when app is launched in develop mode or the Mixpanel is not initialized.
   *
   * @param event_name Name of the event.
   * @param properties Set of properties to include with the event that to be sent.
   * @param optionsOrCallback Optional configuration for this track request.
   * @param callback If provided, the callback function will be called after tracking the event.
   */
  static track(
    event_name: string,
    properties?: Dict,
    optionsOrCallback?: RequestOptions | Callback,
    callback?: Callback,
  ) {
    if (isDevelop() || !this.isInitialized) {
      return
    }

    mixpanel.track(event_name, properties, optionsOrCallback, callback)
  }

  /**
   * Update the configuration of a mixpanel library instance.
   *
   * Do nothing when app is launched in develop mode or the Mixpanel is not initialized.
   *
   * @param config Dictionary of new configuration values to update .
   */
  static setConfig(config: Partial<Config>) {
    if (isDevelop() || !this.isInitialized) {
      return
    }

    mixpanel.set_config(config)
  }
}

MixpanelManager.initialize()
